import { AuthCompany } from 'src/models';

/** GrowthBook flag: header Upgrade CTA (plan / provider targeting lives in GrowthBook rules). */
export const HEADER_PAID_UPGRADE_CTA_FLAG = 'header-paid-upgrade-cta';

/**
 * Attribution passed down to Payment Summary / checkout events so conversions
 * from this CTA are distinguishable from billing-page and upsell-page upgrades.
 */
export const HEADER_PAID_UPGRADE_UPSELL_NAME = 'Header Upgrade CTA';
export const HEADER_PAID_UPGRADE_LINK_LOCATION = 'header';

/** Segment event names for the header paid Upgrade funnel. */
export const HEADER_PAID_UPGRADE_EVENTS = {
  flowStarted: 'Header Upgrade Flow Started',
  modalViewed: 'Header Upgrade Plan Modal Viewed',
  planSelected: 'Header Upgrade Plan Selected',
  modalContinued: 'Header Upgrade Plan Modal Continued',
  modalDismissed: 'Header Upgrade Plan Modal Dismissed',
  flowFailed: 'Header Upgrade Flow Failed',
} as const;

export function isStandardPricingPlan(company?: Pick<AuthCompany, 'pricingPlan'> | null): boolean {
  return !!company?.pricingPlan?.includes('standard');
}

/** Basic → plan picker then confirm; Standard → confirm with Premium preselected. */
export type HeaderPaidUpgradeFlow = 'choose-plan' | 'premium-confirm';

/**
 * Which upgrade UX to open.
 * Prefer billing `currentPlanId` when available — auth `pricingPlan` can still reflect a
 * scheduled future plan (e.g. Premium now, Basic starts later).
 */
export function getHeaderPaidUpgradeFlow(
  company?: Pick<AuthCompany, 'pricingPlan'> | null,
  currentPlanId?: string | null,
): HeaderPaidUpgradeFlow | null {
  const planKey = currentPlanId || company?.pricingPlan;
  if (!planKey) {
    return null;
  }
  if (planKey.includes('basic')) {
    return 'choose-plan';
  }
  if (planKey.includes('standard')) {
    return 'premium-confirm';
  }
  return null;
}

/**
 * Whether the header paid Upgrade CTA should show.
 * GrowthBook owns targeting; also require a live Basic/Standard plan via `currentPlanId`
 * when provided so scheduled downgrades don't leave a dead Upgrade button on Premium.
 */
export function isHeaderPaidUpgradeVisible(
  flagOn: boolean,
  company: AuthCompany | null | undefined,
  isAuthorized: unknown,
  currentPlanId?: string | null,
): boolean {
  if (!flagOn || !isAuthorized || !company) {
    return false;
  }
  const isPaid = company.subscription?.status === 'paid';
  const hasBillingAccess = company.role === 'owner' || !!company.userSettings?.billingAccess;
  if (!isPaid || !hasBillingAccess) {
    return false;
  }
  // When live billing plan is known, it must be Basic/Standard (not Premium).
  if (currentPlanId !== undefined && currentPlanId !== null) {
    return getHeaderPaidUpgradeFlow(company, currentPlanId) !== null;
  }
  return true;
}
